/**
 * VelocityLane Component
 * Velocity stalks below the piano roll for individual & multi-note velocity editing.
 * Fully resizable with draggable top splitter handle, responsive height & synchronized keyboard spacer.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MidiNote } from '../types/midi';
import { ChevronDown, ChevronUp, GripHorizontal, RotateCcw } from 'lucide-react';

interface VelocityLaneProps {
  notes: MidiNote[];
  selectedNoteIds: Set<string>;
  onSelectNote: (id: string, multi: boolean) => void;
  onSetNoteVelocity: (id: string, velocity: number) => void;
  pixelsPerQuarter: number;
  ppq: number;
  totalTicks: number;
  keyboardWidth?: number;
  height?: number;
  onHeightChange?: (h: number) => void;
  scrollLeft?: number;
  onScrollLeft?: (sl: number) => void;
}

export const VelocityLane: React.FC<VelocityLaneProps> = ({
  notes,
  selectedNoteIds,
  onSelectNote,
  onSetNoteVelocity,
  pixelsPerQuarter,
  ppq,
  totalTicks,
  keyboardWidth = 76,
  height: controlledHeight,
  onHeightChange,
  scrollLeft,
  onScrollLeft,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [internalHeight, setInternalHeight] = useState<number>(84);
  const currentHeight = controlledHeight ?? internalHeight;

  const pixelsPerTick = pixelsPerQuarter / ppq;
  const gridWidth = totalTicks * pixelsPerTick;

  const [draggingNoteId, setDraggingNoteId] = useState<string | null>(null);
  const [isResizingLane, setIsResizingLane] = useState<boolean>(false);
  const resizeStartYRef = useRef<number>(0);
  const resizeStartHeightRef = useRef<number>(currentHeight);

  const laneContainerRef = useRef<HTMLDivElement>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);

  // Synchronize horizontal scroll with PianoRoll
  useEffect(() => {
    if (scrollWrapperRef.current && scrollLeft !== undefined) {
      if (Math.abs(scrollWrapperRef.current.scrollLeft - scrollLeft) > 1) {
        scrollWrapperRef.current.scrollLeft = scrollLeft;
      }
    }
  }, [scrollLeft]);

  // Resize Splitter Handle Mouse Down
  const handleSplitterMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizingLane(true);
    resizeStartYRef.current = e.clientY;
    resizeStartHeightRef.current = currentHeight;
    if (collapsed) setCollapsed(false);
  };

  // Global Mouse Move & Up for Height Resizing
  useEffect(() => {
    if (!isResizingLane) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Dragging UP increases height, dragging DOWN decreases height
      const deltaY = resizeStartYRef.current - e.clientY;
      const proposed = Math.max(40, Math.min(260, resizeStartHeightRef.current + deltaY));
      if (onHeightChange) {
        onHeightChange(proposed);
      } else {
        setInternalHeight(proposed);
      }
    };

    const handleMouseUp = () => {
      setIsResizingLane(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingLane, onHeightChange]);

  // Velocity stalk dragging
  const handleBarMouseDown = (e: React.MouseEvent, noteId: string) => {
    e.stopPropagation();
    e.preventDefault();
    if (!selectedNoteIds.has(noteId)) {
      onSelectNote(noteId, e.shiftKey);
    }
    setDraggingNoteId(noteId);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!draggingNoteId || !laneContainerRef.current) return;
      const rect = laneContainerRef.current.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      // Invert: top is 127, bottom is 1
      const normalized = Math.max(0, Math.min(1, 1 - relativeY / currentHeight));
      const newVelocity = Math.max(1, Math.min(127, Math.round(normalized * 127)));
      onSetNoteVelocity(draggingNoteId, newVelocity);
    },
    [draggingNoteId, currentHeight, onSetNoteVelocity]
  );

  const handleMouseUp = useCallback(() => {
    setDraggingNoteId(null);
  }, []);

  useEffect(() => {
    if (draggingNoteId) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggingNoteId, handleMouseMove, handleMouseUp]);

  return (
    <div
      id="velocity-lane-root"
      className="border-t border-[#2D2820] bg-[#181614] flex-shrink-0 select-none flex flex-col relative"
    >
      {/* Draggable Splitter Handle between Piano Roll and Velocity Lane */}
      <div
        id="velocity-lane-resizer"
        onMouseDown={handleSplitterMouseDown}
        onDoubleClick={() => {
          if (onHeightChange) onHeightChange(84);
          else setInternalHeight(84);
        }}
        className={`h-2 -mt-1 cursor-row-resize flex items-center justify-center transition-colors group relative z-20 ${
          isResizingLane ? 'bg-[#F5C767]/40' : 'bg-transparent hover:bg-[#F5C767]/20'
        }`}
        title="Drag up/down to resize Velocity Lane height (Double-click to reset)"
      >
        <div className="w-12 h-1 bg-[#3D362C] rounded-full group-hover:bg-[#F5C767] transition-colors flex items-center justify-center">
          <GripHorizontal className="w-2.5 h-2.5 text-[#A89F91] opacity-70 group-hover:opacity-100" />
        </div>
      </div>

      {/* Header Bar */}
      <div className="flex items-center justify-between px-3 py-0.5 bg-[#181614] border-b border-[#2D2820] text-[11px] text-[#EBE6DB]">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-[#F5C767]">Velocity Lane</span>
          <span className="text-[10px] text-[#A89F91] font-mono">
            {collapsed ? '(Collapsed)' : `(1 - 127 • Height: ${currentHeight}px)`}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {!collapsed && (
            <button
              onClick={() => {
                if (onHeightChange) onHeightChange(84);
                else setInternalHeight(84);
              }}
              className="px-1.5 py-0.5 text-[10px] text-[#A89F91] hover:text-[#F5C767] hover:bg-[#221F1B] transition-colors flex items-center gap-1 rounded"
              title="Reset velocity lane height to default (84px)"
            >
              <RotateCcw className="w-2.5 h-2.5" />
              <span>Reset</span>
            </button>
          )}

          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 hover:bg-[#221F1B] text-[#A89F91] hover:text-[#F5C767] transition-colors rounded"
            title={collapsed ? 'Expand velocity lane' : 'Collapse velocity lane'}
          >
            {collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Main Velocity Canvas */}
      {!collapsed && (
        <div
          ref={scrollWrapperRef}
          onScroll={(e) => onScrollLeft?.(e.currentTarget.scrollLeft)}
          className="flex relative overflow-x-auto overflow-y-hidden daw-scroll"
          style={{ height: `${currentHeight}px` }}
        >
          {/* Left spacer matching dynamically resized piano keys */}
          <div
            style={{ width: `${keyboardWidth}px` }}
            className="flex-shrink-0 bg-[#1C1A17] border-r-2 border-[#2D2820] flex flex-col justify-between py-1 px-1.5 text-[8px] text-[#A89F91] font-mono sticky left-0 z-10 select-none shadow-xs"
          >
            <span className="text-[#F5C767] font-bold">127</span>
            <span className="text-[#A89F91]/80">96</span>
            <span className="text-[#A89F91]/80">64</span>
            <span className="text-[#A89F91]/80">32</span>
            <span className="text-[#F5C767]/90 font-bold">1</span>
          </div>

          {/* Stalks container */}
          <div
            ref={laneContainerRef}
            style={{ width: `${gridWidth}px`, height: `${currentHeight}px` }}
            className="relative flex-shrink-0 bg-[#141210]"
          >
            {/* Guide lines */}
            <div
              className="absolute left-0 right-0 border-b border-[#2D2820]/70 pointer-events-none"
              style={{ top: `${currentHeight * 0.25}px` }}
            />
            <div
              className="absolute left-0 right-0 border-b border-[#2D2820] pointer-events-none"
              style={{ top: `${currentHeight * 0.5}px` }}
            />
            <div
              className="absolute left-0 right-0 border-b border-[#2D2820]/70 pointer-events-none"
              style={{ top: `${currentHeight * 0.75}px` }}
            />

            {/* Velocity stalks */}
            {notes.map((note) => {
              const x = note.startTick * pixelsPerTick;
              const isSelected = selectedNoteIds.has(note.id);
              const stalkHeight = Math.max(4, Math.round((note.velocity / 127) * currentHeight));
              const vel = note.velocity;

              let stemColor = 'bg-[#E5B558] group-hover:bg-[#F5C767]';
              let headColor = 'bg-[#E5B558] border-[#B88218]';
              if (isSelected) {
                stemColor = 'bg-[#FFE699]';
                headColor = 'bg-[#FFE699] border-white ring-2 ring-[#F5C767] shadow-[0_0_8px_rgba(245,199,103,0.8)]';
              } else if (vel >= 100) {
                stemColor = 'bg-[#F5C767]';
                headColor = 'bg-[#F5C767] border-[#D9A33E] shadow-[0_0_6px_rgba(245,199,103,0.5)]';
              } else if (vel >= 70) {
                stemColor = 'bg-[#E5B558]';
                headColor = 'bg-[#E5B558] border-[#B88218]';
              } else {
                stemColor = 'bg-[#C99839]';
                headColor = 'bg-[#C99839] border-[#996D19]';
              }

              return (
                <div
                  key={`vel-${note.id}`}
                  onMouseDown={(e) => handleBarMouseDown(e, note.id)}
                  style={{
                    left: `${x}px`,
                    bottom: 0,
                    height: `${stalkHeight}px`,
                  }}
                  className="group absolute w-4 -ml-2 cursor-ns-resize flex flex-col items-center justify-start z-10"
                  title={`Velocity: ${note.velocity} (Pitch: ${note.pitch})`}
                >
                  {/* Handle Head */}
                  <div
                    className={`w-2.5 h-2.5 rounded-full border shadow-xs transition-transform group-hover:scale-125 ${headColor}`}
                  />
                  {/* Stem */}
                  <div className={`w-[2px] flex-1 rounded-t-sm transition-colors ${stemColor}`} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
